/**
 * RisikoprofilGenerellSection - the generelles Risikoprofil (Ausschreibung Ch 3): one card per
 * Kategorie, each with its atomic catalog fields and a per-card «Bearbeiten → Speichern»
 * (merge-patch). firma_mirror + derived fields render read-only; the besonders schuetzenswerten
 * Felder (Gesundheit/Einkommen) are hidden/disabled per the field gate + an Einwilligung toggle.
 *
 * Behind the `kunden.risikoprofil.generell` Kachel visibility key.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  RISIKOPROFIL_KATEGORIE,
  RISIKOPROFIL_KATEGORIEN_JE_TYP,
  RISIKOPROFIL_KATEGORIE_LABELS,
  GESUNDHEITSPRUEFUNG_STATUS_VALUES,
  GESUNDHEITSPRUEFUNG_STATUS_LABELS,
  EINKOMMENS_BANDBREITE_VALUES,
  EINKOMMENS_BANDBREITE_LABELS,
  risikoprofilFelderFuer,
  countFilledFelder,
} from '@brokly/shared';
import type { RisikoprofilResponse, RisikoprofilPatchBody } from '@brokly/shared';
import { Button } from '../../../../components/ui/Button';
import { RisikoprofilFeldInput } from './RisikoprofilFeldInput';

/** Value equality for a risikoprofil attribute (handles multiselect arrays alongside scalars/null). */
function risikoprofilWertGleich(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) || Array.isArray(b)) {
    const aa = Array.isArray(a) ? a : [];
    const bb = Array.isArray(b) ? b : [];
    return aa.length === bb.length && aa.every((v, i) => v === bb[i]);
  }
  return a === b;
}

export function RisikoprofilGenerellSection({
  profile,
  canWrite,
  isSaving,
  onPatch,
  onEinwilligung,
  schadenSlot,
}: {
  profile: RisikoprofilResponse;
  canWrite: boolean;
  isSaving: boolean;
  onPatch: (body: RisikoprofilPatchBody) => void;
  onEinwilligung: (erfasst: boolean) => void;
  /** Schadenverlauf je Sparte (P2 hardening Q15), rendered inside the Schaden card. */
  schadenSlot?: ReactNode;
}) {
  const profileType = profile.profile_type;
  // P2 Slice 3: prefer the server-resolved catalog (DB Regelwerk if present, else code), falling
  // back to the code catalog for a pre-Slice-3 response that omits `felder`.
  const felder = profile.felder?.length ? profile.felder : risikoprofilFelderFuer(profileType);
  const kategorien = RISIKOPROFIL_KATEGORIEN_JE_TYP[profileType];

  // Per-category edit state: the category key currently in edit, and its working draft.
  const [editingKategorie, setEditingKategorie] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, unknown>>({});

  const startEdit = (kategorie: string) => {
    const vals: Record<string, unknown> = {};
    for (const f of felder.filter((x) => x.kategorie === kategorie)) {
      vals[f.key] = profile.attributes[f.key] ?? null;
    }
    setDraft(vals);
    setEditingKategorie(kategorie);
  };

  const saveEdit = () => {
    // Item 13 MINOR1: PATCH only the fields that actually changed in this edit. The draft is seeded
    // with every field of the category (so the inputs render their current values), so sending the
    // whole draft would re-submit a pre-existing stored value that already exceeds a cap and 400 the
    // entire save on an unrelated edit. Diff against the stored values and send touched-only - the
    // same merge-patch the Police-Ist editor uses.
    const changed: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(draft)) {
      if (!risikoprofilWertGleich(val, profile.attributes[key] ?? null)) changed[key] = val;
    }
    onPatch({ attributes: changed, expected_updated_at: profile.updated_at });
    setEditingKategorie(null);
  };

  return (
    <section aria-labelledby="rp-generell" className="flex flex-col gap-4" data-testid="rp-generell">
      <h3 id="rp-generell" className="sr-only">
        Generelles Risikoprofil
      </h3>

      {kategorien.map((kategorie) => {
        const catFelder = felder.filter((f) => f.kategorie === kategorie);
        const { filled, total } = countFilledFelder(catFelder, profile.attributes);
        const editing = editingKategorie === kategorie;
        const isBesondere = kategorie === RISIKOPROFIL_KATEGORIE.BESONDERE_PERSONENDATEN;

        return (
          <div key={kategorie} className="rounded-lg border bg-card p-4" data-testid={`rp-card-${kategorie}`}>
            <div className="flex items-center justify-between gap-3">
              <h4 className="text-sm font-semibold text-foreground">
                {RISIKOPROFIL_KATEGORIE_LABELS[kategorie]}
              </h4>
              <div className="flex items-center gap-3">
                {total > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {filled}/{total} gespeichert
                  </span>
                )}
                {canWrite && total > 0 && !isBesondere && !editing && (
                  <Button size="sm" variant="outline" onClick={() => startEdit(kategorie)} data-testid={`rp-edit-${kategorie}`}>
                    Bearbeiten
                  </Button>
                )}
                {editing && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => setEditingKategorie(null)}>
                      Abbrechen
                    </Button>
                    <Button size="sm" onClick={saveEdit} disabled={isSaving} data-testid={`rp-save-${kategorie}`}>
                      {isSaving ? 'Speichern…' : 'Speichern'}
                    </Button>
                  </>
                )}
              </div>
            </div>

            {/* firma_mirror (read-only) at the top of the relevant cards. */}
            {profile.stammdaten_mirror && kategorie === RISIKOPROFIL_KATEGORIE.UNTERNEHMENSSTRUKTUR && (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ReadOnly label="Rechtsform" value={profile.stammdaten_mirror.rechtsform} />
                <ReadOnly label="Gruendungsjahr" value={profile.stammdaten_mirror.gruendungsjahr} />
              </div>
            )}
            {profile.stammdaten_mirror && kategorie === RISIKOPROFIL_KATEGORIE.BRANCHE_TAETIGKEIT && (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ReadOnly label="NOGA-Code" value={profile.stammdaten_mirror.noga_code} />
                <ReadOnly label="Zweckbeschreibung" value={profile.stammdaten_mirror.zweckbeschreibung} />
                <ReadOnly label="IGB2B-Hauptbranchen" value={profile.derived.igb2b_hauptbranchen.join(', ') || null} />
              </div>
            )}

            {/* The editable catalog fields of this category. */}
            {catFelder.length > 0 && (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {catFelder.map((def) => (
                  <RisikoprofilFeldInput
                    key={def.key}
                    def={def}
                    value={editing ? draft[def.key] : profile.attributes[def.key]}
                    editing={editing}
                    onChange={(v) => setDraft((d) => ({ ...d, [def.key]: v }))}
                  />
                ))}
              </div>
            )}

            {/* Portfolio-Kennzahlen + M8 KPIs: read-only derived. */}
            {kategorie === RISIKOPROFIL_KATEGORIE.PORTFOLIO_KENNZAHLEN && (
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ReadOnly label="Anzahl aktiver Policen" value={profile.derived.anzahl_aktiver_policen} />
                <ReadOnly label="Anzahl abgedeckter Sparten" value={profile.derived.anzahl_abgedeckter_sparten} />
              </div>
            )}

            {/* Schadenverlauf je Sparte (P2 hardening Q15): below the two remaining Schaden-Katalogfelder. */}
            {kategorie === RISIKOPROFIL_KATEGORIE.SCHADEN_VERTRAGSHISTORIE && schadenSlot}

            {/* Besondere Personendaten: gated columns + Einwilligung (Privatperson). */}
            {isBesondere && (
              <BesondereCard
                profile={profile}
                canWrite={canWrite}
                isSaving={isSaving}
                onPatch={onPatch}
                onEinwilligung={onEinwilligung}
              />
            )}
          </div>
        );
      })}
    </section>
  );
}

function ReadOnly({ label, value }: { label: string; value: string | number | null }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground">{value === null || value === '' ? '-' : String(value)}</span>
    </div>
  );
}

/** The besondere-Personendaten block: gated status/income + the Einwilligung toggle. */
function BesondereCard({
  profile,
  canWrite,
  isSaving,
  onPatch,
  onEinwilligung,
}: {
  profile: RisikoprofilResponse;
  canWrite: boolean;
  isSaving: boolean;
  onPatch: (body: RisikoprofilPatchBody) => void;
  onEinwilligung: (erfasst: boolean) => void;
}) {
  const gesundheitHidden = profile.gated_fields_suppressed.includes('gesundheitspruefung_status');
  const einkommenHidden = profile.gated_fields_suppressed.includes('einkommens_bandbreite');

  return (
    <div className="mt-3 flex flex-col gap-3" data-testid="rp-besondere">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-medium text-muted-foreground">Einwilligung erfasst</span>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            disabled={!canWrite || isSaving}
            checked={profile.einwilligung.erfasst}
            onChange={(e) => onEinwilligung(e.target.checked)}
            data-testid="rp-einwilligung"
            className="size-4"
          />
          {profile.einwilligung.erfasst ? `Ja${profile.einwilligung.datum ? '' : ''}` : 'Nein'}
        </label>
      </div>

      {/* Gesundheitspruefung */}
      {gesundheitHidden ? (
        <p className="text-sm text-muted-foreground" data-testid="rp-gesundheit-hidden">
          Statusflag Gesundheitspruefung: nicht sichtbar (Feldrecht / Einwilligung).
        </p>
      ) : (
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Statusflag Gesundheitspruefung</span>
          <select
            disabled={!canWrite || isSaving}
            value={profile.gesundheitspruefung_status ?? ''}
            onChange={(e) =>
              onPatch({
                gesundheitspruefung_status: (e.target.value || null) as never,
                expected_updated_at: profile.updated_at,
              })
            }
            className="rounded-md border bg-background px-2 py-1 text-sm"
            data-testid="rp-gesundheit"
          >
            <option value="">-</option>
            {GESUNDHEITSPRUEFUNG_STATUS_VALUES.map((v) => (
              <option key={v} value={v}>
                {GESUNDHEITSPRUEFUNG_STATUS_LABELS[v]}
              </option>
            ))}
          </select>
        </label>
      )}

      {/* Einkommens-Bandbreite */}
      {einkommenHidden ? (
        <p className="text-sm text-muted-foreground" data-testid="rp-einkommen-hidden">
          Einkommens-Bandbreite: nicht sichtbar (Feldrecht / Einwilligung).
        </p>
      ) : (
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-muted-foreground">Einkommens-Bandbreite</span>
          <select
            disabled={!canWrite || isSaving}
            value={profile.einkommens_bandbreite ?? ''}
            onChange={(e) =>
              onPatch({
                einkommens_bandbreite: (e.target.value || null) as never,
                expected_updated_at: profile.updated_at,
              })
            }
            className="rounded-md border bg-background px-2 py-1 text-sm"
            data-testid="rp-einkommen"
          >
            <option value="">-</option>
            {EINKOMMENS_BANDBREITE_VALUES.map((v) => (
              <option key={v} value={v}>
                {EINKOMMENS_BANDBREITE_LABELS[v]}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}
